import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isArray, isEmpty, isEqual } from 'lodash'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { IconDownload } from '@tabler/icons-react'
import queryString from 'query-string'
import format from 'date-fns/format'
import classNames from 'classnames'
import { Button } from '@/components/buttons/Button'
import { Menu } from '@/components/navigation/Menu'

import {
  applyFilterAndFetchCalls,
  clearAllFilters,
  fetchCallsFromLatestFilters,
  clearAllFiltersAndFetchCalls,
  downloadCallsCsv,
  downloadCsv,
  fetchSummariesForCallIds,
  loadAllSummariesForSearch,
  loadDefaultCalls,
  loadInitialData,
  setSavedFilterById,
} from '@/reducers/callSearch/callSearch.actions'
import { getChannelTabIndex } from '@/reducers/callSearch/helpers'
import { shouldShowOmniPromotion } from '@/utils/helpers'
import { CustomColumns } from '@/components/tables/CustomColumns/CustomColumns'
import { CALL_SEARCH_TABLE_NAME } from '@/reducers/customColumns/customColumns.constants'
import {
  EXTRACTED_DATA_PARENT,
  filterExtraColumns,
  getChildColumns,
  getCompleteColumns,
  getCompleteColumnsWithScores,
  getCompleteColumnsWithTags,
  getDynamicColumns,
  getMemoizedColumns,
  getScorecardsCustomColumns,
  getSummarySearchAccessors,
  getTagCategoryColumns,
} from '@/reducers/customColumns/customColumns.helpers'
import {
  initialState as callSearchInitialState,
  setDateRangeFilters,
  setFilters,
} from '@/reducers/callSearch/callSearch.redux'
import { SavedFilters } from '@/components/savedFilters/SavedFilters'
import { CALL_EXPLORER_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'
import { fetchSavedFilters } from '@/reducers/savedFilters/savedFilters.actions'
import { Banner } from '@/components/banners/Banner'
import {
  CALL_SEARCH_RESULTS_LIMIT,
  CHANNEL_NAME_PLURAL,
  CHANNEL_TYPE,
  CHANNELS_WITH_CUSTOMER_ID,
  CSV_DOWNLOAD_WARNING,
  channelTabIndexMap,
} from '@/utils/constants'
import { CHANNEL_ICONS } from '@/utils/channelIcons'
import { selectSupportWindowOpenForOrg } from '@/reducers/supportAccess/supportAccess.redux'
import { useShouldHideCustomerId } from '@/utils/hooks/useShouldHideCustomerId'
import { useTogoEnabled } from '@/utils/hooks/useTogoEnabled'

import { CallSearchFilters } from './CallSearchFilters'
import { CallSearchTable } from './components/CallSearchTable'
import { DEFAULT_CALL_COLUMNS } from './callColumns'

import './CallSearchPage.scss'

const CallSearchPage = () => {
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()

  const [rowIdsRendered, setRowIdsRendered] = useState([])
  const {
    organizationid: currentUserOrgId,
    own_organization_id: ownOrganizationId,
    hierarchy_manager: isHierarchyManager,
  } = useSelector((state) => state.currentUser)
  const isBaltoAdmin = currentUserOrgId === 1
  const childOrgSelected = isHierarchyManager && currentUserOrgId !== ownOrganizationId
  const {
    loading,
    calls,
    filters,
    totalCount,
    csvAbortController,
    data: { selectedSavedFilterId, tags: tagCategoriesData },
  } = useSelector((state) => state.callSearch)
  const {
    active_channels: activeChannels = [],
    audio_ingestion_only: isAudioIngestionOnly,
    outbound_campaigns_enabled: currentOrgOutboundCampaignsEnabled,
  } = useSelector((state) => state.currentOrganization)
  const { organizations } = useSelector((state) => state)
  const { customColumns } = useSelector((state) => state.customColumns)
  const savedCustomColumns = customColumns[CALL_SEARCH_TABLE_NAME]
  const { search } = location
  const params = queryString.parse(search)

  useEffect(() => {
    if (!params?.call_ids || isArray(params.call_ids)) {
      return
    }

    history.replace(`/calls/${params.call_ids}`)
  }, [])

  const orgActiveChannels = useMemo(() => {
    if (isBaltoAdmin && filters.organizationId) {
      const selectedOrg = organizations?.find((org) => org.id === filters.organizationId)
      return selectedOrg?.active_channels || []
    }
    return activeChannels
  }, [isBaltoAdmin, filters.organizationId, organizations, activeChannels])

  const outboundCampaignsEnabled = useMemo(() => {
    if (isBaltoAdmin && filters.organizationId) {
      const selectedOrg = organizations?.find((org) => org.id === filters.organizationId)
      return Boolean(selectedOrg?.outbound_campaigns_enabled)
    }
    return Boolean(currentOrgOutboundCampaignsEnabled)
  }, [isBaltoAdmin, filters.organizationId, organizations, currentOrgOutboundCampaignsEnabled])

  // QCER-802: as org 1, a summary request against an unredacted org is a
  // guaranteed 403 unless the customer has an open Balto Support Access window.
  // Asking anyway costs one doomed request per render pass and gives the user
  // nothing, so skip it here the same way the CSV export already does.
  const supportWindowOpen = useSelector((state) =>
    selectSupportWindowOpenForOrg(state, filters.organizationId)
  )
  const summariesUnavailableForOrg = useMemo(() => {
    if (!isBaltoAdmin || currentUserOrgId === filters.organizationId || supportWindowOpen) {
      return false
    }
    const selectedOrg = organizations?.find((org) => org.id === filters.organizationId)
    return selectedOrg?.unredacted_enabled === true
  }, [isBaltoAdmin, currentUserOrgId, filters.organizationId, organizations, supportWindowOpen])

  const togoEnabled = useTogoEnabled(filters.organizationId)

  const shouldHideCustomerId = useShouldHideCustomerId(filters.organizationId)

  const selectedChannel = useMemo(() => {
    if (params?.channel) return params.channel
    if (filters?.channel) return filters.channel
    return CHANNEL_TYPE.CALL
  }, [params?.channel, filters?.channel])

  const defaultChannelTabIndex = getChannelTabIndex(selectedChannel)
  const [channelTabIndex, setChannelTabIndex] = useState(defaultChannelTabIndex)

  useEffect(() => {
    const tabIndex = getChannelTabIndex(selectedChannel)
    setChannelTabIndex(tabIndex)
  }, [selectedChannel])

  const localStorageCallIds = JSON.parse(localStorage.getItem('linkedCallIds'))
  const searchByCallIds = params?.call_ids || localStorageCallIds
  const isSearchByCallIds = Boolean(searchByCallIds)
  const [openFiltersRequested, setOpenFiltersRequested] = useState(false)
  const csvAbortConfirmedRef = useRef(false)

  const showFilters =
    !isSearchByCallIds &&
    !shouldShowOmniPromotion(orgActiveChannels, channelTabIndexMap[channelTabIndex], isBaltoAdmin)

  const showBanner =
    !loading.calls && totalCount > CALL_SEARCH_RESULTS_LIMIT && isEmpty(filters.keywords)
  const isCallExplorerPage = location.pathname.startsWith('/explorer')
  const isCsvDownloadDisabled = loading.csvDownload || loading.calls || isEmpty(calls)

  const updateRowIdsRendered = (rowIds) => {
    if (!isEqual(rowIds, rowIdsRendered)) {
      setRowIdsRendered(rowIds)
    }
  }

  const DEFAULT_COLUMNS = [
    {
      label: 'Call ID',
      accessor: 'call_id',
      isSearchable: true,
      isCopiable: true,
      is_locked: true,
      sticky: true,
      hiddenFor: (call_type) => call_type !== CHANNEL_TYPE.CALL,
      format: (id, row) => {
        // Only append params if not call_id search
        const callExplorerLink = `/calls/${id}${!isSearchByCallIds ? search : ''}`
        if (row.record_type === CHANNEL_TYPE.EMAIL) {
          return ''
        }
        const isAi = row.is_ai_call

        return (
          <Link
            target="_blank"
            rel="noopener noreferrer"
            to={callExplorerLink}
            className={classNames('table-link call-explorer-link', {
              'ai-agent-row-highlight': isAi,
            })}
          >
            {id}
          </Link>
        )
      },
    },
    {
      label: `Message ID`,
      accessor: `message_id`,
      isSearchable: true,
      isCopiable: true,
      is_locked: true,
      sticky: true,
      hiddenFor: (call_type) => call_type === CHANNEL_TYPE.CALL,
      format: (id, row) => {
        if (row.record_type !== CHANNEL_TYPE.CALL) {
          const searchSuffix = (!isSearchByCallIds && search) || ''
          const callExplorerLink = `/messages/${row.message_id}${searchSuffix}`
          return (
            <Link to={callExplorerLink} className="table-link call-explorer-link">
              {id}
            </Link>
          )
        }
        return ''
      },
    },
    ...DEFAULT_CALL_COLUMNS,
  ]

  // TODO: Refactor this into one function instead of several useMemos that all claim the function is the "final complete columns"
  // TODO: Reduce duplication of code (getCompleteColumns, getCompleteColumnsWithScores) - (getDynamicColumns, getScorecardsCustomColumns)
  // TODO: Reduce duplication between this file and PlaylistCallsContent
  const columnsByCallType = useMemo(() => {
    return DEFAULT_COLUMNS.map((column) => {
      if (!column.hiddenFor) column.hiddenFor = () => false
      return {
        ...column,
        is_hidden:
          column.hiddenFor(selectedChannel) ||
          column.is_hidden ||
          (isAudioIngestionOnly && column.audioIngestionOnlyHidden) ||
          (shouldHideCustomerId && column.redactedOrgHidden) ||
          (column.requiresOutboundCampaigns && !outboundCampaignsEnabled) ||
          (column.requiresTogo && !togoEnabled),
      }
    }).filter((column) => !column.is_hidden)
  }, [
    selectedChannel,
    filters,
    rowIdsRendered,
    savedCustomColumns,
    isAudioIngestionOnly,
    shouldHideCustomerId,
    outboundCampaignsEnabled,
    togoEnabled,
  ])
  const columns = useMemo(
    () => getMemoizedColumns(savedCustomColumns, columnsByCallType),
    [savedCustomColumns, columnsByCallType, search]
  )
  const dynamicColumns = useMemo(
    () => getDynamicColumns(calls),
    [columns, calls, rowIdsRendered, savedCustomColumns]
  )
  const dynamicExtractedDataColumns = useMemo(
    () =>
      getDynamicColumns(calls, EXTRACTED_DATA_PARENT).filter(
        (column) => !dynamicColumns.includes(column)
      ),
    [columns, calls, rowIdsRendered, savedCustomColumns, dynamicColumns]
  )
  const dynamicScorecardColumns = useMemo(
    () => getScorecardsCustomColumns(filters.scorecards),
    [calls, columns, rowIdsRendered, savedCustomColumns, filters]
  )
  const dynamicTagCategoryColumns = useMemo(
    () => getTagCategoryColumns(tagCategoriesData),
    [tagCategoriesData]
  )
  const columnsWithSummaries = getCompleteColumns(columns, dynamicColumns)
  const columnsWithExtractedData = getCompleteColumns(
    columnsWithSummaries,
    dynamicExtractedDataColumns,
    EXTRACTED_DATA_PARENT
  )
  const columnsWithScores = getCompleteColumnsWithScores(
    columnsWithExtractedData,
    dynamicScorecardColumns
  )
  const completeColumns = getCompleteColumnsWithTags(columnsWithScores, dynamicTagCategoryColumns)

  const filteredColumns = useMemo(
    () => filterExtraColumns(completeColumns, savedCustomColumns),
    [calls, columns, rowIdsRendered, filters, savedCustomColumns, tagCategoriesData]
  )
  const extraColumns = getChildColumns(completeColumns, savedCustomColumns)
  // END TODO

  // Notetaker columns (call_summary / extracted_data) are only populated for rendered
  // rows, so searching them client-side would only match the current page. When one is
  // searched, load summaries for the whole result set so the search covers the full table.
  const summarySearchAccessors = getSummarySearchAccessors(completeColumns)

  // Start the bulk load as soon as the search popup opens — by the time the user
  // finishes typing, summaries are already loading or loaded.
  const handleTableSearchOpen = (accessor) => {
    if (summarySearchAccessors.includes(accessor)) {
      dispatch(loadAllSummariesForSearch())
    }
  }

  // Keystroke fallback for results that loaded after the popup was opened.
  const handleTableSearch = (accessor, value) => {
    if (!isEmpty(value) && summarySearchAccessors.includes(accessor)) {
      dispatch(loadAllSummariesForSearch())
    }
  }

  const loadingSearchAccessors = loading.summarySearch ? summarySearchAccessors : []

  const handleClearFilters = () => {
    if (isCallExplorerPage) {
      dispatch(clearAllFiltersAndFetchCalls())
    } else {
      dispatch(clearAllFilters())
    }
  }

  const handleSelectSavedFilter = (value) => {
    if (!value) {
      handleClearFilters()
    } else {
      dispatch(setSavedFilterById(value))
    }
  }

  const handleChannelChange = (activeIndex) => {
    if (activeIndex === channelTabIndex) {
      return
    }
    const channel = channelTabIndexMap[activeIndex]
    if (csvAbortController) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(CSV_DOWNLOAD_WARNING)) {
        return
      }
      csvAbortConfirmedRef.current = true
      csvAbortController.abort()
    }
    setChannelTabIndex(activeIndex)
    // Carried onto a channel that captures no customer identity, this returns
    // nothing with the input that set it hidden on the new tab -- an empty
    // result with no visible cause.
    if (!CHANNELS_WITH_CUSTOMER_ID.includes(channel) && !isEmpty(filters.voipCustomerIds)) {
      // Not applyFiltersAndFetchCalls: it runs the payload through mapParamIds,
      // which rebuilds agents/tags/playbooks/keywords from the (absent) query
      // params and would wipe them all on a tab switch.
      dispatch(
        setFilters({
          channel,
          voipCustomerIds: callSearchInitialState.filters.voipCustomerIds,
        })
      )
      dispatch(fetchCallsFromLatestFilters())
      return
    }
    dispatch(applyFilterAndFetchCalls('channel', channel))
  }

  const channelTabItems = Object.values(CHANNEL_TYPE).map((channel, index) => {
    const ChannelIcon = CHANNEL_ICONS[channel]
    return {
      accessor: channel,
      icon: <ChannelIcon />,
      label: CHANNEL_NAME_PLURAL[channel],
      active: channelTabIndex === index,
      onClick: () => handleChannelChange(index),
    }
  })

  useEffect(() => {
    const getInitialSearch = async (searchParams, paramsWithDefaults = params) => {
      dispatch(fetchSavedFilters(CALL_EXPLORER_FILTER_TYPE))

      if (localStorageCallIds) {
        await dispatch(
          loadInitialData(
            queryString.stringify({ ...paramsWithDefaults, call_ids: localStorageCallIds })
          )
        )
        localStorage.removeItem('linkedCallIds')
      } else {
        await dispatch(loadInitialData(searchParams))
      }
    }

    const startOfToday = format(new Date(), "yyyy-MM-dd'T00:00:00'xxx")
    const endOfToday = format(new Date(), "yyyy-MM-dd'T23:59:59'xxx")

    // If URL search params exist, use those
    if (!isEmpty(search)) {
      const paramsWithDefaultValues = {
        ...params,
        startDate: params.startDate || startOfToday,
        endDate: params.endDate || endOfToday,
        organizationId: params.organizationId || currentUserOrgId,
      }
      const searchWithDefaults =
        params.startDate && params.endDate ? search : queryString.stringify(paramsWithDefaultValues)

      getInitialSearch(searchWithDefaults, paramsWithDefaultValues)
    } else {
      // If none exist, load the default saved filter or the initial filters if none exist
      dispatch(setDateRangeFilters({ startDate: startOfToday, endDate: endOfToday }))
      dispatch(loadDefaultCalls())
    }
  }, [])

  useEffect(() => {
    if (!isEmpty(rowIdsRendered) && !summariesUnavailableForOrg) {
      const callsRenderedWithoutSummariesLoaded = calls
        .filter((call) => rowIdsRendered.includes(call.call_id)) // Only fetches summaries for calls that have been rendered
        .filter(({ call_summary }) => call_summary === undefined)
        .map(({ call_id }) => call_id)

      if (!isEmpty(callsRenderedWithoutSummariesLoaded)) {
        dispatch(fetchSummariesForCallIds(callsRenderedWithoutSummariesLoaded))
      }
    }
  }, [rowIdsRendered, savedCustomColumns, calls, summariesUnavailableForOrg])

  useEffect(() => {
    if (!csvAbortController) {
      csvAbortConfirmedRef.current = false
      return undefined
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Warn when navigating away via sidebar links, back button, etc.
    // Tab switches use window.confirm() directly in handleChannelChange and
    // set csvAbortConfirmedRef to skip the duplicate prompt from history.block.
    const unblock = history.block(() => {
      if (csvAbortConfirmedRef.current) {
        return undefined
      }
      return CSV_DOWNLOAD_WARNING
    })

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      unblock()
      csvAbortController.abort()
    }
  }, [csvAbortController])

  return (
    <>
      <header className="data-grid-header page-header">
        <h1 className="page-header__title" data-testid="calls-header">
          Explorer
        </h1>
        <div className="flex-align-center medium-gap">
          <Button
            icon={<IconDownload />}
            data-testid="csv-download-button"
            onClick={() => {
              if (totalCount > CALL_SEARCH_RESULTS_LIMIT) {
                dispatch(downloadCallsCsv())
              } else {
                const allCallIds = calls.map((call) => call.call_id).filter(Boolean)
                dispatch(downloadCsv(completeColumns, allCallIds))
              }
            }}
            loading={loading.csvDownload}
            disabled={isCsvDownloadDisabled}
          >
            {`Download ${CHANNEL_NAME_PLURAL[selectedChannel]} (CSV)`}
          </Button>
          <CustomColumns
            tableName={CALL_SEARCH_TABLE_NAME}
            defaultColumns={columnsByCallType}
            extraColumns={extraColumns}
          />
          {!childOrgSelected && (
            <SavedFilters
              filterType={CALL_EXPLORER_FILTER_TYPE}
              selectedSavedFilterId={selectedSavedFilterId}
              currentChannel={selectedChannel}
              orgActiveChannels={orgActiveChannels}
              handleSelectSavedFilter={handleSelectSavedFilter}
              filterFormOptions={[
                'name',
                'isDefault',
                'filterPropertiesHeader',
                'channel',
                'dateRange',
                'agents',
                'tags',
                'playbooks',
                'voipCampaigns',
                'voipCustomerIds',
                'callDuration',
                'maxCallDuration',
                'includeCallsWithoutAudio',
                'isWin',
                'keywordHeader',
                'keywords',
                'keywordOptions.side',
                'keywordOptions.logic',
                'dispositionsHeader',
                'dispositions',
                'playbookPropertiesHeader',
                'checklist',
                'deck',
                'notifications',
                'postcall',
                'scorecards',
                'scorecardThreshold',
                'scoredStatus',
                'endingSentiment',
                'attachments',
                'direction',
                'minMessageCount',
                'maxMessageCount',
                'agentType',
              ]}
            />
          )}
        </div>
      </header>
      {!isSearchByCallIds && (
        <header>
          <div className="call-search-tab">
            <Menu menuStyle="bar" items={channelTabItems} />
          </div>
        </header>
      )}
      {showFilters && (
        <header className="data-grid-subheader">
          <CallSearchFilters
            params={params}
            childOrgSelected={childOrgSelected}
            unlimitedDateRange
            openFiltersRequested={openFiltersRequested}
            onOpenFiltersHandled={() => setOpenFiltersRequested(false)}
          />
        </header>
      )}

      {showBanner && (
        <div className="results-limit-banner-wrapper">
          <Banner
            caution
            dataTestId="results-limit-banner"
            content={
              <>
                {`Showing ${calls.length.toLocaleString()} of ${totalCount.toLocaleString()} ${CHANNEL_NAME_PLURAL[selectedChannel].toLowerCase()}. Add filters to find what you're looking for. `}
                <Button link onClick={() => setOpenFiltersRequested(true)}>
                  Add Filters
                </Button>
              </>
            }
          />
        </div>
      )}

      <CallSearchTable
        columns={filteredColumns}
        loading={loading}
        filters={filters}
        calls={calls}
        handleClearFilters={handleClearFilters}
        updateRowIdsRendered={updateRowIdsRendered}
        organizationId={
          isSearchByCallIds ? params.organizationId : filters.organizationId || currentUserOrgId
        }
        orgActiveChannels={orgActiveChannels}
        tagCategoriesData={tagCategoriesData}
        isBaltoAdmin={isBaltoAdmin}
        onSearch={handleTableSearch}
        onSearchOpen={handleTableSearchOpen}
        loadingSearchAccessors={loadingSearchAccessors}
      />
    </>
  )
}

export default CallSearchPage
